import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import RXService from '@Services/RxService';
import { HEATMAP_OBSERVABLE_TOPICS, HEATMAP_SPLITTER_CHANGE_STATES } from '@Pages/Heatmap/constants';

const MIN_VALID_POSITION_WITH_STRUCTURE = 132;
const MIN_VALID_POSITION = 200;

const setElementTopOffset = (el, topOffset) => {
  // eslint-disable-next-line no-param-reassign
  el.style.top = `${topOffset}px`;
};

function HorizontalSplitter({ showStructure }) {
  const backdropRef = useRef();
  const splitterRef = useRef();

  const instanceRefs = useRef({
    touchedAt: null,
    initialY: null,
    lastValidY: null,
    moveFired: null,
  });

  const [isColumnHeadersResizing, setIsColumnHeadersResizing] = useState(false);
  const [splitterParams, setSplitterParams] = useState({});

  const mouseMoveHandler = useCallback(
    (e) => {
      if (!instanceRefs.current.moveFired) {
        RXService.next(HEATMAP_OBSERVABLE_TOPICS.horizontalSplitter, HEATMAP_SPLITTER_CHANGE_STATES.moved);
        instanceRefs.current.moveFired = true;
      }

      const minValidPosition = showStructure ? MIN_VALID_POSITION_WITH_STRUCTURE : MIN_VALID_POSITION;
      const y = e.screenY - instanceRefs.current.touchedAt;
      const splitterHeight = splitterParams.height;
      const isValidPosition = y + splitterHeight > minValidPosition && y + splitterHeight < backdropRef.current.offsetHeight / 2;

      if (isValidPosition) {
        instanceRefs.current.lastValidY = e.screenY;
        setElementTopOffset(splitterRef.current, y);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [splitterParams]
  );

  const mouseUpHandler = useCallback(() => {
    RXService.next(HEATMAP_OBSERVABLE_TOPICS.horizontalSplitter, HEATMAP_SPLITTER_CHANGE_STATES.released, {
      heightDelta: instanceRefs.current.lastValidY - instanceRefs.current.initialY,
    });

    setIsColumnHeadersResizing(false);
  }, []);

  const horizontalSplitterChanged = useCallback((state, stateParams) => {
    if (state === HEATMAP_SPLITTER_CHANGE_STATES.activated) {
      instanceRefs.current = {
        // coordinate inside of splitter where mouse hit
        touchedAt: stateParams.eventParams.screenY - stateParams.splitterParams.top,
        initialY: stateParams.eventParams.screenY,
        lastValidY: stateParams.eventParams.screenY,
        moveFired: false,
      };

      setIsColumnHeadersResizing(true);
      setSplitterParams(stateParams.splitterParams);
    }
  }, []);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.horizontalSplitter, horizontalSplitterChanged)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [horizontalSplitterChanged]);

  useEffect(() => {
    if (isColumnHeadersResizing) {
      window.addEventListener('mousemove', mouseMoveHandler, false);
      window.addEventListener('mouseup', mouseUpHandler, false);
    }
    return () => {
      if (isColumnHeadersResizing) {
        window.removeEventListener('mousemove', mouseMoveHandler, false);
        window.removeEventListener('mouseup', mouseUpHandler, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isColumnHeadersResizing]);

  const splitterStyles = {
    height: splitterParams.height,
    top: splitterParams.top,
  };

  const splitterInnerStyles = {
    width: splitterParams.width,
    height: splitterParams.height,
    left: splitterParams.left,
  };

  return (
    <div
      className={cn('heatmap__splitter-backdrop heatmap__backdrop-horizontal', { 'u-els-display-none': !isColumnHeadersResizing })}
      ref={backdropRef}>
      <div className="heatmap__resize-splitter-horizontal" ref={splitterRef} style={splitterStyles}>
        <div className="heatmap__resize-splitter-horizontal__inner" style={splitterInnerStyles} />
      </div>
    </div>
  );
}

HorizontalSplitter.propTypes = {
  showStructure: PropTypes.bool.isRequired,
};

export default HorizontalSplitter;
